import { z } from "zod";
import { opaqueIdSchema, sha256Schema } from "./opaque.js";
const captureOpaqueIdSchema = z
    .string()
    .refine((value) => opaqueIdSchema.safeParse(value).success ||
    /^(?:response|book|prompt|schedule|delivery|asset|revision)-[a-f0-9]{12,64}$/u.test(value), "Expected an opaque capture identifier");
const uniqueOpaqueIds = (maximum) => z
    .array(opaqueIdSchema)
    .max(maximum)
    .superRefine((values, context) => {
    if (new Set(values).size !== values.length)
        context.addIssue({ code: "custom", message: "Identifiers must be unique." });
});
const isoDateSchema = z.string().date();
export const myStoryTemporalPrecisionSchema = z.enum([
    "EXACT_DAY",
    "MONTH",
    "YEAR",
    "DECADE",
    "RANGE",
    "LIFE_STAGE",
    "UNKNOWN",
]);
export const myStoryTemporalCertaintySchema = z.enum([
    "CERTAIN",
    "APPROXIMATE",
    "REMEMBERED",
    "DISPUTED",
    "UNKNOWN",
]);
export const myStoryTemporalPositionSchema = z
    .object({
    label: z.string().trim().min(1).max(160),
    precision: myStoryTemporalPrecisionSchema,
    certainty: myStoryTemporalCertaintySchema,
    earliest: isoDateSchema.nullable(),
    latest: isoDateSchema.nullable(),
    sourceClaimIds: uniqueOpaqueIds(50),
})
    .strict()
    .superRefine((value, context) => {
    const unknown = value.precision === "UNKNOWN";
    if (unknown !== (value.earliest === null && value.latest === null))
        context.addIssue({
            code: "custom",
            path: ["earliest"],
            message: "Unknown temporal positions must omit both bounds; known positions require both.",
        });
    if (value.earliest === null || value.latest === null)
        return;
    if (value.earliest > value.latest)
        context.addIssue({
            code: "custom",
            path: ["latest"],
            message: "The latest possible date cannot precede the earliest possible date.",
        });
    if (value.precision === "EXACT_DAY" && value.earliest !== value.latest)
        context.addIssue({
            code: "custom",
            path: ["latest"],
            message: "An exact day must use the same earliest and latest date.",
        });
    if (value.precision === "MONTH" && value.earliest.slice(0, 7) !== value.latest.slice(0, 7))
        context.addIssue({
            code: "custom",
            path: ["latest"],
            message: "Month precision must remain within one calendar month.",
        });
    if (value.precision === "YEAR" && value.earliest.slice(0, 4) !== value.latest.slice(0, 4))
        context.addIssue({
            code: "custom",
            path: ["latest"],
            message: "Year precision must remain within one calendar year.",
        });
    if (value.precision === "DECADE" &&
        Math.floor(Number(value.earliest.slice(0, 4)) / 10) !==
            Math.floor(Number(value.latest.slice(0, 4)) / 10))
        context.addIssue({
            code: "custom",
            path: ["latest"],
            message: "Decade precision must remain within one decade.",
        });
});
export const myStorySourceReferenceSchema = z
    .object({
    responseId: captureOpaqueIdSchema,
    bookId: captureOpaqueIdSchema,
    prompt: z
        .object({
        promptId: captureOpaqueIdSchema,
        catalogPromptId: z.string().trim().min(1).max(223).nullable(),
        catalogVersion: z
            .string()
            .regex(/^\d{4}\.\d{2}\.\d{2}$/u)
            .nullable(),
    })
        .strict(),
    delivery: z
        .object({
        scheduleId: captureOpaqueIdSchema,
        deliveryAttemptId: captureOpaqueIdSchema,
        channel: z.enum(["EMAIL", "TEXT", "CAPTURE_LINK", "PHONE_CALL"]),
    })
        .strict(),
    sourceAsset: z
        .object({
        assetId: captureOpaqueIdSchema,
        kind: z.enum(["TEXT", "EXISTING_UPLOAD", "BROWSER_RECORDING"]),
        objectKey: z.string().trim().min(1).max(1_024),
        versionId: z.string().trim().min(1).max(1_024).nullable(),
        checksumSha256: sha256Schema,
        contentType: z.string().trim().min(1).max(255),
        byteSize: z.number().int().nonnegative(),
        capturedAt: z.string().datetime(),
    })
        .strict(),
    transcript: z
        .object({
        state: z.enum(["NOT_APPLICABLE", "QUEUED", "READY", "FAILED"]),
        transcriptRevisionId: captureOpaqueIdSchema.nullable(),
        providerMode: z.enum(["NONE", "SANDBOX", "WORKING"]),
    })
        .strict(),
    responseRevision: z
        .object({
        revisionId: captureOpaqueIdSchema,
        number: z.number().int().positive(),
        parentRevisionId: captureOpaqueIdSchema.nullable(),
        createdAt: z.string().datetime(),
    })
        .strict(),
    sourceSpan: z
        .object({
        kind: z.enum(["RESPONSE_TEXT", "TRANSCRIPT"]),
        start: z.number().int().nonnegative(),
        end: z.number().int().positive(),
        exactText: z.string().min(1).max(20_000),
        sourceTextChecksumSha256: sha256Schema,
    })
        .strict(),
    consent: z
        .object({
        termsVersion: z.string().trim().min(1).max(64),
        captureConfirmedAt: z.string().datetime(),
        recordingConsent: z.enum(["NOT_REQUIRED", "GRANTED"]),
    })
        .strict(),
    responseCreatedAt: z.string().datetime(),
})
    .strict()
    .superRefine((value, context) => {
    if ((value.prompt.catalogPromptId === null) !== (value.prompt.catalogVersion === null))
        context.addIssue({
            code: "custom",
            path: ["prompt", "catalogVersion"],
            message: "Catalog prompt identity and version must be present together.",
        });
    if (value.responseRevision.number === 1 && value.responseRevision.parentRevisionId !== null)
        context.addIssue({
            code: "custom",
            path: ["responseRevision", "parentRevisionId"],
            message: "The first response revision cannot have a parent.",
        });
    if (value.responseRevision.number > 1 && value.responseRevision.parentRevisionId === null)
        context.addIssue({
            code: "custom",
            path: ["responseRevision", "parentRevisionId"],
            message: "Corrected response revisions must retain their parent revision.",
        });
    if (value.sourceSpan.start >= value.sourceSpan.end)
        context.addIssue({
            code: "custom",
            path: ["sourceSpan", "end"],
            message: "A source span must have a positive length.",
        });
    if (value.sourceSpan.end - value.sourceSpan.start !== value.sourceSpan.exactText.length)
        context.addIssue({
            code: "custom",
            path: ["sourceSpan", "exactText"],
            message: "The exact source text length must match its offsets.",
        });
    if (value.sourceSpan.kind === "TRANSCRIPT" && value.transcript.transcriptRevisionId === null)
        context.addIssue({
            code: "custom",
            path: ["transcript", "transcriptRevisionId"],
            message: "Transcript spans require an exact transcript revision.",
        });
    if ((value.transcript.state === "READY") !== (value.transcript.transcriptRevisionId !== null))
        context.addIssue({
            code: "custom",
            path: ["transcript", "transcriptRevisionId"],
            message: "Transcript revision identity must agree with transcript readiness.",
        });
    if (value.sourceSpan.kind === "TRANSCRIPT" && value.transcript.state !== "READY")
        context.addIssue({
            code: "custom",
            path: ["transcript", "state"],
            message: "Transcript spans can only cite a ready transcript revision.",
        });
    if ((value.sourceAsset.kind === "TEXT" && value.consent.recordingConsent !== "NOT_REQUIRED") ||
        (value.sourceAsset.kind !== "TEXT" && value.consent.recordingConsent !== "GRANTED"))
        context.addIssue({
            code: "custom",
            path: ["consent", "recordingConsent"],
            message: "Recorded and uploaded sources require explicit recording consent.",
        });
    if (Date.parse(value.consent.captureConfirmedAt) > Date.parse(value.sourceAsset.capturedAt) ||
        Date.parse(value.sourceAsset.capturedAt) > Date.parse(value.responseCreatedAt) ||
        Date.parse(value.sourceAsset.capturedAt) > Date.parse(value.responseRevision.createdAt))
        context.addIssue({
            code: "custom",
            path: ["responseCreatedAt"],
            message: "Consent, capture, response, and revision timestamps are out of order.",
        });
});
export const myStoryVisibilitySchema = z.enum([
    "PRIVATE_TO_STORYTELLER",
    "SHARED_WITH_EDITORS",
    "SHARED_WITH_BOOK",
    "EXCLUDED",
]);
export const myStorySensitiveClassificationSchema = z.enum([
    "NONE",
    "HEALTH",
    "GRIEF",
    "TRAUMA",
    "SEXUALITY",
    "FINANCIAL",
    "LEGAL_ALLEGATION",
    "FAMILY_CONFLICT",
    "MINOR",
    "OTHER_PRIVATE_DETAIL",
]);
export const myStoryClaimKindSchema = z.enum([
    "EVENT",
    "IDENTITY",
    "DATE",
    "PLACE",
    "QUOTATION",
    "RELATIONSHIP",
    "SENSITIVE_DETAIL",
    "OTHER",
]);
export const myStoryClaimEvidenceStatusSchema = z.enum(["SUPPORTED", "DISPUTED", "NEEDS_REVIEW"]);
export const myStoryClaimLifecycleSchema = z.enum(["ACTIVE", "SUPERSEDED", "EXCLUDED"]);
export const myStoryPersonSchema = z
    .object({
    personId: opaqueIdSchema,
    displayName: z.string().trim().min(1).max(160),
    aliases: z.array(z.string().trim().min(1).max(160)).max(20),
    disambiguation: z.string().trim().min(1).max(240),
})
    .strict();
export const myStoryClaimSchema = z
    .object({
    claimId: opaqueIdSchema,
    kind: myStoryClaimKindSchema,
    text: z.string().trim().min(1).max(4_000),
    subjectPersonIds: uniqueOpaqueIds(50),
    evidenceStatus: myStoryClaimEvidenceStatusSchema,
    lifecycle: myStoryClaimLifecycleSchema,
    supersedesClaimId: opaqueIdSchema.nullable(),
    visibility: myStoryVisibilitySchema,
    sensitivity: myStorySensitiveClassificationSchema,
    sensitivityNote: z.string().trim().min(1).max(1_000).nullable(),
    sources: z.array(myStorySourceReferenceSchema).min(1).max(50),
})
    .strict()
    .superRefine((value, context) => {
    if ((value.sensitivity === "NONE") !== (value.sensitivityNote === null))
        context.addIssue({
            code: "custom",
            path: ["sensitivityNote"],
            message: "Sensitive claims require a note; non-sensitive claims must omit it.",
        });
    if (value.lifecycle === "EXCLUDED" && value.visibility !== "EXCLUDED")
        context.addIssue({
            code: "custom",
            path: ["visibility"],
            message: "Excluded claims must also have excluded visibility.",
        });
    if (value.lifecycle !== "EXCLUDED" && value.visibility === "EXCLUDED")
        context.addIssue({
            code: "custom",
            path: ["lifecycle"],
            message: "Excluded visibility requires an excluded lifecycle.",
        });
    if (value.lifecycle === "ACTIVE" &&
        value.evidenceStatus === "SUPPORTED" &&
        !value.sources.length)
        context.addIssue({
            code: "custom",
            path: ["sources"],
            message: "Active supported claims require source evidence.",
        });
});
export const myStoryLifeEventSchema = z
    .object({
    eventId: opaqueIdSchema,
    title: z.string().trim().min(1).max(200),
    sequence: z.number().int().nonnegative(),
    temporal: myStoryTemporalPositionSchema,
    claimIds: uniqueOpaqueIds(100).min(1),
    personIds: uniqueOpaqueIds(100),
    placeLabels: z.array(z.string().trim().min(1).max(200)).max(50),
})
    .strict();
export const myStoryContradictionResolutionSchema = z
    .object({
    disposition: z.enum(["PRESENT_SIDE_BY_SIDE", "ACCEPT_CLAIM", "OMIT"]),
    acceptedClaimIds: uniqueOpaqueIds(20),
    note: z.string().trim().min(1).max(2_000),
    resolvedBy: opaqueIdSchema,
    resolvedAt: z.string().datetime(),
})
    .strict();
export const myStoryContradictionGroupSchema = z
    .object({
    contradictionId: opaqueIdSchema,
    topic: z.string().trim().min(1).max(240),
    claimIds: uniqueOpaqueIds(20).min(2),
    status: z.enum(["OPEN", "RESOLVED"]),
    resolution: myStoryContradictionResolutionSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if ((value.status === "RESOLVED") !== (value.resolution !== null))
        context.addIssue({
            code: "custom",
            path: ["resolution"],
            message: "Resolved contradictions require a resolution; open contradictions cannot have one.",
        });
    if (value.resolution?.disposition === "ACCEPT_CLAIM" &&
        value.resolution.acceptedClaimIds.length !== 1)
        context.addIssue({
            code: "custom",
            path: ["resolution", "acceptedClaimIds"],
            message: "Accepting a claim requires exactly one accepted claim.",
        });
    if (value.resolution &&
        value.resolution.acceptedClaimIds.some((claimId) => !value.claimIds.includes(claimId)))
        context.addIssue({
            code: "custom",
            path: ["resolution", "acceptedClaimIds"],
            message: "A resolution can only accept claims from its contradiction group.",
        });
});
export const myStoryVoiceAnchorSchema = z
    .object({
    voiceAnchorId: opaqueIdSchema,
    speakerPersonId: opaqueIdSchema,
    exactText: z.string().min(1).max(4_000),
    qualities: z.array(z.string().trim().min(1).max(80)).min(1).max(20),
    visibility: myStoryVisibilitySchema,
    source: myStorySourceReferenceSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.exactText !== value.source.sourceSpan.exactText)
        context.addIssue({
            code: "custom",
            path: ["exactText"],
            message: "Voice anchors must retain the exact source span text.",
        });
});
export const myStoryChapterEvidencePlanSchema = z
    .object({
    chapterId: opaqueIdSchema,
    title: z.string().trim().min(1).max(200),
    position: z.number().int().nonnegative(),
    eventIds: uniqueOpaqueIds(100),
    primaryEventIds: uniqueOpaqueIds(100),
    claimIds: uniqueOpaqueIds(500),
})
    .strict()
    .superRefine((value, context) => {
    if (value.primaryEventIds.some((eventId) => !value.eventIds.includes(eventId)))
        context.addIssue({
            code: "custom",
            path: ["primaryEventIds"],
            message: "Every primary event must also be assigned to the chapter.",
        });
});
export const myStoryEvidenceFoundationSchema = z
    .object({
    version: z.literal(1),
    bookId: opaqueIdSchema,
    storytellerPersonId: opaqueIdSchema,
    people: z.array(myStoryPersonSchema).min(1).max(1_000),
    claims: z.array(myStoryClaimSchema).max(10_000),
    events: z.array(myStoryLifeEventSchema).max(2_000),
    contradictions: z.array(myStoryContradictionGroupSchema).max(1_000),
    voiceAnchors: z.array(myStoryVoiceAnchorSchema).max(500),
    chapters: z.array(myStoryChapterEvidencePlanSchema).max(250),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.updatedAt < value.createdAt)
        context.addIssue({
            code: "custom",
            path: ["updatedAt"],
            message: "The evidence foundation cannot be updated before it is created.",
        });
});
export const myStoryPocSourceSchema = z
    .object({
    sourceId: opaqueIdSchema,
    bookId: opaqueIdSchema,
    speakerPersonId: opaqueIdSchema,
    session: z.number().int().min(1).max(20),
    title: z.string().trim().min(1).max(200),
    kind: z.enum(["INTERVIEW_TRANSCRIPT", "LETTER", "ARTIFACT_NOTE"]),
    revisionId: opaqueIdSchema,
    revisionNumber: z.number().int().positive(),
    parentRevisionId: opaqueIdSchema.nullable(),
    capturedAt: z.string().datetime(),
    checksumSha256: sha256Schema,
    text: z.string().min(80).max(30_000),
    consent: z.literal("GRANTED"),
    visibility: myStoryVisibilitySchema,
    synthetic: z.literal(true),
})
    .strict()
    .superRefine((source, context) => {
    if (source.revisionNumber === 1 && source.parentRevisionId !== null)
        context.addIssue({
            code: "custom",
            path: ["parentRevisionId"],
            message: "An initial POC source cannot have a parent revision.",
        });
    if (source.revisionNumber > 1 && source.parentRevisionId === null)
        context.addIssue({
            code: "custom",
            path: ["parentRevisionId"],
            message: "A corrected POC source must retain its parent revision.",
        });
});
export const myStoryPocSourcePacketSchema = z
    .object({
    version: z.literal(1),
    bookId: opaqueIdSchema,
    title: z.literal("The Long Way Home"),
    subtitle: z.literal("A Life in Five Places"),
    storytellerPersonId: opaqueIdSchema,
    storytellerName: z.literal("Eleanor “Nell” Brooks"),
    sources: z.array(myStoryPocSourceSchema).min(5).max(20),
})
    .strict()
    .superRefine((packet, context) => {
    const ids = packet.sources.map(({ sourceId }) => sourceId);
    if (new Set(ids).size !== ids.length)
        context.addIssue({
            code: "custom",
            path: ["sources"],
            message: "Source IDs must be unique.",
        });
    packet.sources.forEach((source, index) => {
        if (source.bookId !== packet.bookId)
            context.addIssue({
                code: "custom",
                path: ["sources", index, "bookId"],
                message: "Every source must belong to the POC book.",
            });
        if (source.speakerPersonId !== packet.storytellerPersonId)
            context.addIssue({
                code: "custom",
                path: ["sources", index, "speakerPersonId"],
                message: "Every My Story source must retain the storyteller identity.",
            });
    });
});
//# sourceMappingURL=my-story.js.map